import React, { memo, useMemo, useEffect } from 'react';
import { Validators } from '@core/utils/form';
import { InvoiceDiscountType, InvoiceDiscountLabel } from '@business/entities/TransactionInvoice';
import { FormSelect, FormInput } from '@core/components/form';
import { CommonService } from '@business/services';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { Row, Col, Collapse } from 'reactstrap';
import { ProposalDiscountForm } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { ProposalDiscountType } from '@business/entities/proposal';
import { resolve } from '@core/utils/ioc';
import { Blocs } from '@business/blocs';

export interface ProposalDiscountProps {
  form: ProposalDiscountForm;
  /** Also ask for the discount type (Settings > Discount Types) and a description: Work Order discounts. */
  withType?: boolean;
}

export const ProposalDiscount = memo((props: ProposalDiscountProps) => {
  const { form, withType = false } = props;
  const formValue = useNonNilObservable(form.value$);
  const typeItems = useMemo(
    () => CommonService.selectItems([InvoiceDiscountType.percentage, InvoiceDiscountType.fixed], i => InvoiceDiscountLabel[i]),
    [],
  );

  const discountTypeBloc = useMemo(() => resolve(Blocs.discountType), []);
  const discountTypes = useObservable(discountTypeBloc.items$, []);
  const discountTypeItems = useMemo(() => CommonService.selectItems(discountTypes, 'title', 'id', false), [discountTypes]);

  useEffect(() => {
    if (withType) {
      const subscription = discountTypeBloc.fetchDiscountTypes().subscribe();
      return () => subscription.unsubscribe();
    }
  }, [withType, discountTypeBloc]);

  useEffect(() => {
    form.controls.type.setConfig({ required: formValue.isDiscount });
    form.controls.type.setConfig({ required: formValue.isDiscount });
    if (formValue.type === ProposalDiscountType.percentage) {
      form.controls.value.setConfig({ validators: [Validators.min(0), Validators.max(100)] });
    } else {
      form.controls.value.setConfig({ validators: [Validators.min(0)] });
    }
  }, [form.controls.type, form.controls.value, formValue.isDiscount, formValue.type]);

  return (
    <div style={{ textAlign: 'right' }}>
      <a className="text-primary " onClick={() => form.patchValue({ isDiscount: !formValue.isDiscount })}>
        {formValue.isDiscount ? 'Remove Discount' : 'Add Discount'}
      </a>
      <Collapse isOpen={formValue.isDiscount}>
        {withType && (
          <Row className="mt-2" style={{ textAlign: 'left' }}>
            <Col>
              <FormSelect control={form.controls.discountTypeId} items={discountTypeItems} isClearable />
            </Col>
            <Col>
              <FormInput control={form.controls.description} />
            </Col>
          </Row>
        )}
        <Row className="mt-2" style={withType ? { textAlign: 'left' } : undefined}>
          <Col>
            <FormSelect control={form.controls.type} items={typeItems} />
          </Col>
          <Col>
            <FormInput type="number" control={form.controls.value} parseToFloat />
          </Col>
        </Row>
      </Collapse>
    </div>
  );
});
